import Phaser from 'phaser';

// Twin-stick touch controls for phones/tablets.
//
//   left half of the screen   floating movement stick; fully extended = run
//   right half                aim stick; past FIRE_THRESHOLD = fire
//
// Lives in the HUD scene (screen coordinates, drawn above the game). The
// game scene reads `state` every tick and converts it to the same button
// flags the keyboard produces, so the protocol and server are untouched.

export interface TouchState {
  active: boolean; // touch is the primary input on this device
  moveX: number; // -1..1, screen axes (right / down positive)
  moveY: number;
  moveMag: number; // 0..1
  aiming: boolean; // right stick currently held
  aim: number; // radians, last aim direction
  aimMag: number; // 0..1 deflection of the aim stick (a grenade's throw distance)
  fire: boolean;
}

const STICK_RADIUS = 64; // px of travel for full deflection
const KNOB_RADIUS = 26;
const DEAD_ZONE = 0.15;
const FIRE_THRESHOLD = 0.5;

interface Stick {
  pointerId: number; // -1 when idle
  baseX: number;
  baseY: number;
  dx: number; // -1..1
  dy: number;
  mag: number;
}

export class TouchControls {
  readonly state: TouchState = { active: true, moveX: 0, moveY: 0, moveMag: 0, aiming: false, aim: 0, aimMag: 0, fire: false };

  private readonly left: Stick = { pointerId: -1, baseX: 0, baseY: 0, dx: 0, dy: 0, mag: 0 };
  private readonly right: Stick = { pointerId: -1, baseX: 0, baseY: 0, dx: 0, dy: 0, mag: 0 };
  private readonly gfx: Phaser.GameObjects.Graphics;

  constructor(private readonly scene: Phaser.Scene) {
    // Phaser starts with 1 touch pointer + mouse; we need two fingers at once
    // plus a spare for taps.
    scene.input.addPointer(3);
    this.gfx = scene.add.graphics().setDepth(50);

    scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onUp, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
      scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
      scene.input.off(Phaser.Input.Events.POINTER_UP, this.onUp, this);
      scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onUp, this);
    });
  }

  private onDown(p: Phaser.Input.Pointer): void {
    const half = this.scene.scale.width / 2;
    const stick = p.x < half ? this.left : this.right;
    if (stick.pointerId !== -1) return; // that side already has a finger
    stick.pointerId = p.id;
    stick.baseX = p.x;
    stick.baseY = p.y;
    stick.dx = stick.dy = stick.mag = 0;
    this.publish();
  }

  private onMove(p: Phaser.Input.Pointer): void {
    const stick = p.id === this.left.pointerId ? this.left : p.id === this.right.pointerId ? this.right : null;
    if (!stick) return;
    let dx = (p.x - stick.baseX) / STICK_RADIUS;
    let dy = (p.y - stick.baseY) / STICK_RADIUS;
    let mag = Math.hypot(dx, dy);
    if (mag > 1) {
      dx /= mag;
      dy /= mag;
      mag = 1;
    }
    stick.dx = dx;
    stick.dy = dy;
    stick.mag = mag;
    this.publish();
  }

  private onUp(p: Phaser.Input.Pointer): void {
    if (p.id === this.left.pointerId) this.reset(this.left);
    else if (p.id === this.right.pointerId) this.reset(this.right);
    this.publish();
  }

  private reset(s: Stick): void {
    s.pointerId = -1;
    s.dx = s.dy = s.mag = 0;
  }

  private publish(): void {
    const s = this.state;
    const l = this.left;
    const r = this.right;
    if (l.mag > DEAD_ZONE) {
      s.moveX = l.dx;
      s.moveY = l.dy;
      s.moveMag = l.mag;
    } else {
      s.moveX = s.moveY = s.moveMag = 0;
    }
    s.aiming = r.pointerId !== -1 && r.mag > DEAD_ZONE;
    if (s.aiming) s.aim = Math.atan2(r.dy, r.dx);
    s.aimMag = r.pointerId !== -1 ? r.mag : 0;
    s.fire = r.pointerId !== -1 && r.mag > FIRE_THRESHOLD;
  }

  // Called from the HUD scene's update.
  draw(): void {
    const g = this.gfx;
    g.clear();
    this.drawStick(this.left, false);
    this.drawStick(this.right, true);
  }

  private drawStick(s: Stick, isAim: boolean): void {
    if (s.pointerId === -1) return;
    const g = this.gfx;
    const firing = isAim && s.mag > FIRE_THRESHOLD;
    g.lineStyle(2, 0xffffff, 0.35).strokeCircle(s.baseX, s.baseY, STICK_RADIUS);
    if (isAim) g.lineStyle(1, 0xffffff, 0.2).strokeCircle(s.baseX, s.baseY, STICK_RADIUS * FIRE_THRESHOLD);
    g.fillStyle(firing ? 0xe07a5a : 0xffffff, firing ? 0.7 : 0.45);
    g.fillCircle(s.baseX + s.dx * STICK_RADIUS, s.baseY + s.dy * STICK_RADIUS, KNOB_RADIUS);
  }
}
